import { MouseEventHandler, useState } from "react";
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import { Box, IconButton, makeStyles, Typography } from "@material-ui/core";
import { PhotoMetadata } from "./photoMetadata";
import { ResultsAnalysisData } from './resultsAnalysisData';
import { LocationData } from "./locationData";
import { observer } from "mobx-react";
import { useStore } from "@roc/client-portal-shared/hooks";
import { formatDate } from "@roc/feature-utils";
import { PhotoGalleryImage, PhotoGalleryModal, PhotoCover } from "@roc/ui";
import { ReactComponent as FullScreenIcon } from '../assets/fullScreen.svg';
import { ReportPhoto } from "./reportPhoto";
import { Flag, FlagOutlined } from "@material-ui/icons";
import reportedPhoto from '../assets/reportedPhoto.png';

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gridGap: "1rem",
    height: "100%",
    width: "100%",
  },
  scrollable: {
    overflowY: "auto",
    overflowX: "hidden",
    flexGrow: 1,
    padding: "0",
  },
  cancelIcon: {
    color: "#dd3030",
    fontSize: "2.5rem",
  },
  checkIcon: {
    color: "#1ACF77",
    fontSize: "2.5rem",
  },
  checkIconWarning: {
    color: '#FB8C00',
    fontSize: '2.5rem',
  },
  overlayPhotoContainer: {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 15,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
  },
  photoDetailButton: {
    backgroundColor: "black",
    borderRadius: "5px",
    padding: "7px"
  },
  photoReportedIconContainer: {
    backgroundColor: "#dd3030",
    borderRadius: "5px",
    padding: "7px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  photoReportedIcon: {
    color: "white",
    fontSize: "20px",
  },
  photoDetailIcon: {
    color: "white",
    height: "20px",
    width: "20px",
  },
  ellipsis: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
    maxWidth: '100%',
  },
}));

export const PhotoDetail = observer(() => {
  const classes = useStyles();
  const { inspectionStore } = useStore();
  const [photosModalOpen, setPhotosModalOpen] = useState(false);
  const { inspectionPhotosStore, inspectionDetails, inspectionPhotoSelected } = inspectionStore;

  const inspectionTask = inspectionStore.inspectionTasks.find(
    task => task.inspectionTaskId === inspectionPhotoSelected.inspectionTaskId
  );
  const lastUpdatedDate = inspectionPhotoSelected?.captureTimeIso
    ? new Date(inspectionPhotoSelected?.captureTimeIso)
    : null;

  const modalPhotos: PhotoGalleryImage[] = Object.values(
    inspectionPhotosStore.inspectionTaskPhotos || {}
  ).reduce<PhotoGalleryImage[]>((acc, photos) => {
    if (Array.isArray(photos) && photos.length > 0) {
      acc.push(
        ...photos.map(photo => ({
          key: `${photo.inspectionPhotoId}`,
          url: photo.reported ? reportedPhoto : photo.image,
          preferredRotation: photo.reported ? 0 : photo.preferredRotation,
          caption: photo.caption,
        }))
      );
    }
    return acc;
  }, []);

  const photoStatusIcon = inspectionPhotosStore.photoHasIssues(inspectionPhotoSelected) ? (
    <CancelIcon className={classes.cancelIcon} />
  ) : inspectionPhotosStore.photoHasManualValidation(inspectionPhotoSelected) ? (
    <CheckCircleIcon className={classes.checkIconWarning} />
  ) : (
    <CheckCircleIcon className={classes.checkIcon} />
  );

  const photoReportedIcon = (
    <Box className={classes.photoReportedIconContainer}>
      <FlagOutlined className={classes.photoReportedIcon} />
    </Box>
  );

  const openTaskPhotos: MouseEventHandler<Element> = (e) => {
    setPhotosModalOpen(true);
    inspectionPhotosStore.fetchPhoto(inspectionPhotoSelected?.inspectionPhotoId);
    e.preventDefault();
    e.stopPropagation();
  }

  const overlayPhotoButtons = (
    <Box className={classes.overlayPhotoContainer}>
      {photoStatusIcon}
      {inspectionPhotoSelected.reported && photoReportedIcon}
      {modalPhotos.length > 0 && (<IconButton className={classes.photoDetailButton} onClick={openTaskPhotos}>
        <FullScreenIcon className={classes.photoDetailIcon} />
      </IconButton>)}
    </Box>
  );

  const onPhotoRotationHandler = (inspectionPhotoId: string, rotationDegrees: number) => {
    inspectionPhotosStore.setPhotoPreferredRotation(inspectionPhotoId, rotationDegrees);
  }

  const onPhotoChange = (key: string) => {
    const inspectionPhotoId = parseInt(key);
    inspectionPhotosStore.fetchPhoto(inspectionPhotoId);
  }

  return (
    <>
      <Box className={classes.root}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" position={"sticky"} top={0} bgcolor="#fff" zIndex={100} paddingY={4}>
          <Box display="flex" flexDirection="column" maxWidth="calc(100% - 48px)"> {/* Leaves space for CloseIcon */}
            <Box display="flex" alignItems="center" gridGap="1rem" maxWidth="100%">
              <Typography variant="h5" className={classes.ellipsis}>
                {inspectionTask?.description}
              </Typography>
            </Box>
            <Typography variant="body2">
              Last updated on: {formatDate(lastUpdatedDate)}
            </Typography>
          </Box>
          <IconButton onClick={() => inspectionStore.setInspectionPhotoSelected(null)}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable Content */}
        <Box className={classes.scrollable} display="flex" flexDirection="column" gridGap="2rem">
          <Box style={{ position: "relative" }}>
            {overlayPhotoButtons}
            <PhotoCover
              url={inspectionPhotoSelected.reported ? reportedPhoto : inspectionPhotoSelected.image}
              rotationDegrees={inspectionPhotoSelected.reported ? 0 : inspectionPhotoSelected.preferredRotation || 0}
              width="100%"
              height="400px"
              borderRadius="16px"
              onClick={openTaskPhotos}
              caption={inspectionPhotoSelected.caption}
            />
            {inspectionPhotosStore.canReportPhotos && <ReportPhoto inspectionPhoto={inspectionPhotoSelected} />}
          </Box>

          <LocationData inspectionDetails={inspectionDetails} inspectionTask={inspectionTask} inspectionPhoto={inspectionPhotoSelected} />
          <ResultsAnalysisData inspectionDetails={inspectionDetails} inspectionPhoto={inspectionPhotoSelected} />
          <PhotoMetadata inspectionPhoto={inspectionPhotoSelected} />
        </Box>
      </Box>
      <PhotoGalleryModal
        open={photosModalOpen}
        onClose={() => setPhotosModalOpen(false)}
        photos={modalPhotos}
        enableZoom
        selectedPhotoKey={`${inspectionPhotoSelected?.inspectionPhotoId}`}
        title="Photos"
        onPhotoRotation={onPhotoRotationHandler}
        onPhotoChange={onPhotoChange}
      />
    </>
  );
});
